import { useEffect, useState } from "react";

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Modal, Button, SuccessNotice, FormField, TextInput } from "@a2z/ui";
import type { StoreSocialLink } from "@/lib/api/types";
import { useUpdateStoreSettingsMutation } from "@/modules/settings/services/useUpdateStoreSettingsMutation";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  storeSlug: string;
  urls: StoreSocialLink[];
};

const PLATFORMS = [
  "instagram",
  "tiktok",
  "facebook",
  "x",
  "youtube",
  "website",
] as const;

type Platform = (typeof PLATFORMS)[number];

const PLATFORM_META: Record<
  Platform,
  { labelKey: string; placeholder: string }
> = {
  instagram: {
    labelKey: keys.seller.socialInstagramLabel,
    placeholder: "https://instagram.com/yourbrand",
  },
  tiktok: {
    labelKey: keys.seller.socialTiktokLabel,
    placeholder: "https://tiktok.com/@yourbrand",
  },
  facebook: {
    labelKey: keys.seller.socialFacebookLabel,
    placeholder: "https://facebook.com/yourbrand",
  },
  x: {
    labelKey: keys.seller.socialXLabel,
    placeholder: "https://x.com/yourbrand",
  },
  youtube: {
    labelKey: keys.seller.socialYoutubeLabel,
    placeholder: "https://youtube.com/@yourbrand",
  },
  website: {
    labelKey: keys.seller.socialWebsiteLabel,
    placeholder: "https://yourbrand.com",
  },
};

const urlPattern = /^https?:\/\/.+/i;

function seedDraft(urls: StoreSocialLink[]): Record<Platform, string> {
  const next = {} as Record<Platform, string>;
  for (const platform of PLATFORMS) {
    next[platform] = urls.find((entry) => entry.type === platform)?.url ?? "";
  }
  return next;
}

export function BrandingSocialLinksSheet({
  isOpen,
  onClose,
  storeSlug,
  urls,
}: Props) {
  const t = useTranslations();
  const { mutateAsync: updateStoreSettings, isPending: isSaving } =
    useUpdateStoreSettingsMutation();
  const [draft, setDraft] = useState<Record<Platform, string>>(() =>
    seedDraft(urls),
  );
  const [errorField, setErrorField] = useState<Platform | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seed the draft from the latest values each time the sheet opens
      setDraft(seedDraft(urls));
      setErrorField(null);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, urls]);

  const currentValue = (platform: Platform): string =>
    urls.find((entry) => entry.type === platform)?.url ?? "";

  const isDirty = PLATFORMS.some(
    (platform) => draft[platform].trim() !== currentValue(platform),
  );

  const handleSave = async (): Promise<void> => {
    setError(null);
    setErrorField(null);
    setSuccess(false);

    for (const platform of PLATFORMS) {
      const value = draft[platform].trim();
      if (value.length > 0 && !urlPattern.test(value)) {
        setErrorField(platform);
        setError(t(keys.seller.socialUrlInvalid));
        return;
      }
    }

    const nextUrls: StoreSocialLink[] = PLATFORMS.filter((platform) =>
      draft[platform].trim(),
    ).map((platform) => ({ type: platform, url: draft[platform].trim() }));

    try {
      await updateStoreSettings({ storeSlug, urls: nextUrls });
      setSuccess(true);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={t(keys.seller.socialLinksSectionTitle)}
      closeLabel={t(keys.common.cancel)}
      showFooterWhen={isDirty}
      footerActions={
        <Button
          onClick={() => void handleSave()}
          disabled={isSaving || !isDirty}
          variant="primary"
          size="md"
        >
          {isSaving ? t(keys.common.loading) : t(keys.common.save)}
        </Button>
      }
    >
      <p className="text-sm text-[color:var(--text-muted)]">
        {t(keys.seller.socialLinksHelper)}
      </p>

      {success ? (
        <SuccessNotice title={t(keys.seller.socialLinksSavedNotice)} />
      ) : null}

      {PLATFORMS.map((platform) => {
        const meta = PLATFORM_META[platform];
        const fieldHasError = errorField === platform;
        return (
          <FormField
            key={platform}
            name={`social-${platform}`}
            label={t(meta.labelKey)}
            error={fieldHasError ? (error ?? undefined) : undefined}
          >
            <TextInput
              name={`social-${platform}`}
              inputMode="url"
              dir="ltr"
              placeholder={meta.placeholder}
              value={draft[platform]}
              hasError={fieldHasError}
              onChange={(event) => {
                setSuccess(false);
                setError(null);
                setErrorField(null);
                const nextValue = event.target.value;
                setDraft((prev) => ({ ...prev, [platform]: nextValue }));
              }}
            />
          </FormField>
        );
      })}
    </Modal>
  );
}
